import { useTranslation } from 'react-i18next';
import {
  getMarketingImage,
  getAllMarketingImages,
  hasMarketingImage,
  getAvailableMarketingFeatures,
} from '@/utils/marketingImages';

/**
 * Hook for accessing localized marketing images
 * Automatically uses the current language from the translation system
 */
export function useMarketingImages() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  /**
   * Get a marketing image for a specific feature using current language
   * @param feature - Feature key (e.g., 'dataImport', 'analytics')
   * @returns Full path to the localized marketing screenshot
   */
  const getImage = (feature: string): string => {
    return getMarketingImage(feature, currentLanguage);
  };

  /**
   * Get all marketing images for the current language
   * @returns Object mapping feature names to localized image paths
   */
  const getAllImages = (): Record<string, string> => {
    return getAllMarketingImages(currentLanguage);
  };

  /**
   * Check if a marketing image exists for a feature in current language
   * @param feature - Feature key
   * @returns boolean indicating if image exists
   */
  const hasImage = (feature: string): boolean => {
    return hasMarketingImage(feature, currentLanguage);
  };

  /**
   * Get available marketing features that have images
   * @returns Array of feature keys
   */
  const getAvailableFeatures = (): string[] => {
    return getAvailableMarketingFeatures();
  };

  return {
    getImage,
    getAllImages,
    hasImage,
    getAvailableFeatures,
    currentLanguage,
  };
}
